/**
 * Professional Error Handling Utilities
 * Provides graceful error handling with professional error messages
 */

export interface ErrorDetails {
  code: string;
  message: string;
  userMessage: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'network' | 'validation' | 'api' | 'system' | 'user';
  retryable: boolean;
  timestamp: string;
  context?: Record<string, any>;
}

export interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  retryableErrors: string[];
}

export const defaultRetryConfig: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
  retryableErrors: ['NETWORK_ERROR', 'TIMEOUT', 'SERVER_ERROR', 'RATE_LIMITED']
};

/**
 * Professional error messages for different error types
 */
export const errorMessages = {
  // Network errors
  NETWORK_ERROR: {
    code: 'NETWORK_ERROR',
    message: 'Network connection failed',
    userMessage: 'Unable to connect to StablePay services. Please check your internet connection and try again.',
    severity: 'medium' as const,
    category: 'network' as const,
    retryable: true
  },
  TIMEOUT: {
    code: 'TIMEOUT',
    message: 'Request timeout',
    userMessage: 'The request is taking longer than expected. Please try again.',
    severity: 'medium' as const,
    category: 'network' as const,
    retryable: true
  },
  OFFLINE: {
    code: 'OFFLINE',
    message: 'Device is offline',
    userMessage: 'You are currently offline. Some features may be limited until connection is restored.',
    severity: 'medium' as const,
    category: 'network' as const,
    retryable: true
  },

  // API errors
  SERVER_ERROR: {
    code: 'SERVER_ERROR',
    message: 'Internal server error',
    userMessage: 'A temporary service issue occurred. Our team has been notified and is working to resolve it.',
    severity: 'high' as const,
    category: 'api' as const,
    retryable: true
  },
  RATE_LIMITED: {
    code: 'RATE_LIMITED',
    message: 'Rate limit exceeded',
    userMessage: 'Too many requests. Please wait a moment before trying again.',
    severity: 'medium' as const,
    category: 'api' as const,
    retryable: true
  },
  UNAUTHORIZED: {
    code: 'UNAUTHORIZED',
    message: 'Authentication failed',
    userMessage: 'Your session has expired. Please log in again to continue.',
    severity: 'high' as const,
    category: 'api' as const,
    retryable: false
  },
  FORBIDDEN: {
    code: 'FORBIDDEN',
    message: 'Access denied',
    userMessage: 'You do not have permission to perform this action.',
    severity: 'medium' as const,
    category: 'api' as const,
    retryable: false
  },
  NOT_FOUND: {
    code: 'NOT_FOUND',
    message: 'Resource not found',
    userMessage: 'The requested information could not be found.',
    severity: 'low' as const,
    category: 'api' as const,
    retryable: false
  },

  // Validation errors
  INVALID_INPUT: {
    code: 'INVALID_INPUT',
    message: 'Invalid input provided',
    userMessage: 'Please check your input and try again.',
    severity: 'low' as const,
    category: 'validation' as const,
    retryable: false
  },
  AMOUNT_TOO_LOW: {
    code: 'AMOUNT_TOO_LOW',
    message: 'Amount below minimum',
    userMessage: 'The payment amount is below the minimum required. Please enter a higher amount.',
    severity: 'low' as const,
    category: 'validation' as const,
    retryable: false
  },
  AMOUNT_TOO_HIGH: {
    code: 'AMOUNT_TOO_HIGH',
    message: 'Amount exceeds maximum',
    userMessage: 'The payment amount exceeds the maximum allowed. Please contact support for large transactions.',
    severity: 'medium' as const,
    category: 'validation' as const,
    retryable: false
  },
  INVALID_STABLECOIN: {
    code: 'INVALID_STABLECOIN',
    message: 'Unsupported stablecoin',
    userMessage: 'The selected stablecoin is not supported. Please choose USDT, USDC, HKD₮, or CNH₮.',
    severity: 'low' as const,
    category: 'validation' as const,
    retryable: false
  },

  // Provider errors
  PROVIDER_UNAVAILABLE: {
    code: 'PROVIDER_UNAVAILABLE',
    message: 'Payment provider unavailable',
    userMessage: 'The selected payment provider is temporarily unavailable. We are automatically switching to an alternative provider.',
    severity: 'medium' as const,
    category: 'system' as const,
    retryable: true
  },
  ALL_PROVIDERS_DOWN: {
    code: 'ALL_PROVIDERS_DOWN',
    message: 'All providers unavailable',
    userMessage: 'All payment providers are currently unavailable. Please try again in a few minutes.',
    severity: 'critical' as const,
    category: 'system' as const,
    retryable: true
  },

  // System errors
  UNKNOWN_ERROR: {
    code: 'UNKNOWN_ERROR',
    message: 'Unknown error occurred',
    userMessage: 'An unexpected error occurred. Please try again or contact support if the problem persists.',
    severity: 'medium' as const,
    category: 'system' as const,
    retryable: true
  }
};

/**
 * Error Handler Class
 */
export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorLog: ErrorDetails[] = [];
  private maxLogSize = 100;

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Handle and classify errors
   */
  handleError(error: any, context?: Record<string, any>): ErrorDetails {
    const errorDetails = this.classifyError(error, context);
    this.logError(errorDetails);
    return errorDetails;
  }

  /**
   * Classify error and return structured error details
   */
  private classifyError(error: any, context?: Record<string, any>): ErrorDetails {
    const timestamp = new Date().toISOString();

    // Handle network errors
    if (!navigator.onLine) {
      return {
        ...errorMessages.OFFLINE,
        timestamp,
        context
      };
    }

    // Handle fetch/axios errors
    if (error?.name === 'TypeError' && error?.message?.includes('fetch')) {
      return {
        ...errorMessages.NETWORK_ERROR,
        timestamp,
        context
      };
    }

    // Handle timeout errors
    if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
      return {
        ...errorMessages.TIMEOUT,
        timestamp,
        context
      };
    }

    // Handle HTTP status errors
    if (error?.response?.status) {
      const status = error.response.status;
      switch (status) {
        case 401:
          return { ...errorMessages.UNAUTHORIZED, timestamp, context };
        case 403:
          return { ...errorMessages.FORBIDDEN, timestamp, context };
        case 404:
          return { ...errorMessages.NOT_FOUND, timestamp, context };
        case 429:
          return { ...errorMessages.RATE_LIMITED, timestamp, context };
        case 500:
        case 502:
        case 503:
        case 504:
          return { ...errorMessages.SERVER_ERROR, timestamp, context };
      }
    }

    // Handle validation errors
    if (error?.code && errorMessages[error.code as keyof typeof errorMessages]) {
      return {
        ...errorMessages[error.code as keyof typeof errorMessages],
        timestamp,
        context
      };
    }

    // Default to unknown error
    return {
      ...errorMessages.UNKNOWN_ERROR,
      timestamp,
      context,
      message: error?.message || 'Unknown error occurred'
    };
  }

  /**
   * Log error for debugging and monitoring
   */
  private logError(errorDetails: ErrorDetails): void {
    // Add to in-memory log
    this.errorLog.unshift(errorDetails);
    
    // Maintain log size
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog = this.errorLog.slice(0, this.maxLogSize);
    }

    // Console logging based on severity
    const logMethod = errorDetails.severity === 'critical' ? 'error' :
                     errorDetails.severity === 'high' ? 'error' :
                     errorDetails.severity === 'medium' ? 'warn' : 'info';

    console[logMethod]('StablePay Error:', {
      code: errorDetails.code,
      message: errorDetails.message,
      userMessage: errorDetails.userMessage,
      severity: errorDetails.severity,
      category: errorDetails.category,
      timestamp: errorDetails.timestamp,
      context: errorDetails.context
    });

    // In a real application, you would send critical errors to monitoring service
    if (errorDetails.severity === 'critical') {
      this.reportCriticalError(errorDetails);
    }
  }

  /**
   * Report critical errors to monitoring service
   */
  private reportCriticalError(errorDetails: ErrorDetails): void {
    // In demo mode, just log to console
    console.error('CRITICAL ERROR REPORTED:', errorDetails);
    
    // In production, this would send to monitoring service like Sentry, DataDog, etc.
    // Example:
    // Sentry.captureException(new Error(errorDetails.message), {
    //   tags: { code: errorDetails.code, severity: errorDetails.severity },
    //   extra: errorDetails.context
    // });
  }

  /**
   * Get recent errors for debugging
   */
  getRecentErrors(limit: number = 10): ErrorDetails[] {
    return this.errorLog.slice(0, limit);
  }

  /**
   * Clear error log
   */
  clearErrorLog(): void {
    this.errorLog = [];
  }

  /**
   * Get error statistics
   */
  getErrorStats(): {
    total: number;
    bySeverity: Record<string, number>;
    byCategory: Record<string, number>;
    recent: ErrorDetails[];
  } {
    const bySeverity: Record<string, number> = {};
    const byCategory: Record<string, number> = {};

    this.errorLog.forEach(error => {
      bySeverity[error.severity] = (bySeverity[error.severity] || 0) + 1;
      byCategory[error.category] = (byCategory[error.category] || 0) + 1;
    });

    return {
      total: this.errorLog.length,
      bySeverity,
      byCategory,
      recent: this.errorLog.slice(0, 5)
    };
  }
}

/**
 * Retry mechanism with exponential backoff
 */
export class RetryManager {
  static async withRetry<T>(
    operation: () => Promise<T>,
    config: Partial<RetryConfig> = {}
  ): Promise<T> {
    const finalConfig = { ...defaultRetryConfig, ...config };
    let lastError: any;

    for (let attempt = 1; attempt <= finalConfig.maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        
        const errorHandler = ErrorHandler.getInstance();
        const errorDetails = errorHandler.handleError(error, { attempt });

        // Don't retry if error is not retryable
        if (!errorDetails.retryable) {
          throw error;
        }

        // Don't retry on last attempt
        if (attempt === finalConfig.maxAttempts) {
          break;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          finalConfig.baseDelay * Math.pow(finalConfig.backoffMultiplier, attempt - 1),
          finalConfig.maxDelay
        );

        console.log(`Retrying operation in ${delay}ms (attempt ${attempt}/${finalConfig.maxAttempts})`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();